import { useCallback, useMemo } from 'react';
import { useForm, FormProvider } from 'react-hook-form';

import { useLoggingMutation } from 'lib/apollo/apollo-hooks';
import SimpleModal from 'lib/modal/simple-modal';
import {
  SimpleModalContentStyled,
  SimpleModalVerticalActionsStyled,
} from 'lib/modal/simple-modal.styles';
import {
  COMPLETION_STATUS_NOT_STARTED,
  COMPLETION_STATUS_COMPLETED,
} from 'global/constants';
import { InlineServerError } from 'approot/account/server-error';
import { useSnackbar } from 'approot/shared/snackbar-notification/snackbar-notification.hooks';
import {
  PStyled,
  AposStyled,
  Heading16,
} from 'approot/shared/styles/typography.style';
import {
  SecondaryButton,
  LinkButton,
} from 'approot/shared/styles/buttons/buttons';
import { PrimaryButton, NegativeButton } from 'approot/shared/styles/buttons';
import DatePickerRangeFields from 'approot/shared/date-picker/date-picker-range-fields';
import {
  toDatePickerFormat,
  toIsoDateFormat,
} from 'approot/shared/date-picker/date-picker.utils';
import { useSimpleModalState } from 'approot/shared/modal/modal.hooks';
import { useCurrentClassVar } from 'approot/classes/classes.hooks';
import { pushDataTrackerEvent } from 'approot/shared/data-tracker/data-tracker-push';
import {
  EVENT_ACTION_CLOSE,
  EVENT_ACTION_OPEN,
  EVENT_CATEGORY_CONTENT,
  EVENT_TARGET_IDENTIFIER_CONTENT_EDIT_COMPLETE,
  EVENT_TARGET_IDENTIFIER_CONTENT_REMOVE_COMPLETE,
  EVENT_TARGET_TYPE_MODAL,
} from 'approot/shared/data-tracker/data-events.constants';
import { useDuration } from 'approot/shared/hooks/duration.hooks';
import { START_DATE_FIELD, END_DATE_FIELD } from './complete.constants';
import {
  UPDATE_LESSON_IN_PLANNER,
  UPDATE_ASSESSMENT_IN_PLANNER,
} from './complete.graphql';
import type {
  UpdateLessonInPlannerMutation,
  UpdateLessonInPlannerMutationVariables,
} from './complete.graphql.generated';
import type {
  UpdateAssessmentInPlannerMutation,
  UpdateAssessmentInPlannerMutationVariables,
} from './complete.graphql.generated';
import type { CompletionFragment } from '../planner/manage/planner.graphql.generated';
import {
  pushInCompleteEvent,
  pushRemoveCompleteFollow,
  pushEditCompleteFollow,
  pushEditCompleteEvent,
} from './complete.utils';
import { spacing4 } from 'approot/shared/styles/spacing.style';
import { getTermStartAndEndDates } from 'approot/shared/logic/terms';

type FormData = {
  [START_DATE_FIELD]: string;
  [END_DATE_FIELD]: string;
};

type Props = {
  completion: CompletionFragment | null;
  contentId: number;
  unitId: number;
  contentName: string;
  isLesson: boolean;
  isInPlanner: boolean;
  closeModal: () => void;
};

const Incomplete = ({
  completion,
  contentId,
  unitId,
  contentName,
  isLesson,
  isInPlanner,
  closeModal,
}: Props) => {
  const { showSuccess } = useSnackbar();
  const currentClass = useCurrentClassVar();
  const { getDurationOpen, resetDuration } = useDuration();

  const today = new Date();
  const baseDate = new Date(today.getFullYear(), 0, 1);
  const { start, end } = useMemo(
    () => getTermStartAndEndDates(currentClass?.terms),
    [currentClass]
  );

  const completionStartDate = toDatePickerFormat(
    completion?.startDate || baseDate
  );
  const completionEndDate = toDatePickerFormat(completion?.endDate || baseDate);

  const formMethods = useForm<FormData>({
    mode: 'onChange',
    defaultValues: {
      [START_DATE_FIELD]: completionStartDate,
      [END_DATE_FIELD]: completionEndDate,
    },
  });
  const { handleSubmit } = formMethods;

  const { openModal: openConfirmModal, isVisible: isConfirmModalVisible } =
    useSimpleModalState();

  const withCloseEventData = (close: () => void) => () => {
    pushDataTrackerEvent(EVENT_CATEGORY_CONTENT, EVENT_ACTION_CLOSE, {
      target_type: EVENT_TARGET_TYPE_MODAL,
      target_identifier: isConfirmModalVisible
        ? EVENT_TARGET_IDENTIFIER_CONTENT_REMOVE_COMPLETE
        : EVENT_TARGET_IDENTIFIER_CONTENT_EDIT_COMPLETE,
      duration: getDurationOpen(),
      follow_action: '',
    });
    resetDuration();
    close();
  };

  const [
    updateLessonInPlanner,
    { loading: lessonCompletionLoading, error: lessonCompletionError },
  ] = useLoggingMutation<
    UpdateLessonInPlannerMutation,
    UpdateLessonInPlannerMutationVariables
  >(UPDATE_LESSON_IN_PLANNER);

  const [
    updateAssessmentInPlanner,
    { loading: assessmentCompletionLoading, error: assessmentCompletionError },
  ] = useLoggingMutation<
    UpdateAssessmentInPlannerMutation,
    UpdateAssessmentInPlannerMutationVariables
  >(UPDATE_ASSESSMENT_IN_PLANNER);

  const onComplete = useCallback(
    (completion: CompletionFragment) => {
      closeModal();
      showSuccess('CompletionFragment Date Updated');
      pushEditCompleteEvent(
        isLesson,
        contentId,
        completion.id!,
        completion.startDate!,
        completion.endDate!
      );
    },
    [contentId, isLesson, closeModal, showSuccess]
  );

  const onSubmit = useCallback(
    (data: FormData) => {
      const startDateIso = toIsoDateFormat(data[START_DATE_FIELD]);
      const endDateIso = toIsoDateFormat(data[END_DATE_FIELD]);

      if (!currentClass) return;

      if (startDateIso && endDateIso && startDateIso <= endDateIso) {
        if (isLesson) {
          updateLessonInPlanner({
            variables: {
              input: {
                classId: currentClass.id,
                lessonId: contentId,
                status: COMPLETION_STATUS_COMPLETED,
                startDate: new Date(startDateIso),
                endDate: new Date(endDateIso),
                unitId,
              },
            },
          })
            .then(res => {
              if (res.data?.updateLessonInPlanner.lesson.completion) {
                onComplete(res.data.updateLessonInPlanner.lesson.completion);
              }
            })
            .catch(() => {});
        } else {
          updateAssessmentInPlanner({
            variables: {
              input: {
                classId: currentClass.id,
                assessmentId: contentId,
                status: COMPLETION_STATUS_COMPLETED,
                startDate: new Date(startDateIso),
                endDate: new Date(endDateIso),
                unitId,
              },
            },
          })
            .then(res => {
              if (res.data?.updateAssessmentInPlanner.assessment.completion) {
                onComplete(
                  res.data.updateAssessmentInPlanner.assessment.completion
                );
              }
            })
            .catch(() => {});
        }
      }

      pushEditCompleteFollow();
    },
    [
      contentId,
      isLesson,
      unitId,
      currentClass,
      updateLessonInPlanner,
      updateAssessmentInPlanner,
      onComplete,
    ]
  );

  const handleMarkIncomplete = useCallback(() => {
    if (!currentClass) return;

    if (isLesson) {
      updateLessonInPlanner({
        variables: {
          input: {
            classId: currentClass.id,
            lessonId: contentId,
            status: COMPLETION_STATUS_NOT_STARTED,
            unitId,
          },
        },
      }).then(res => {
        closeModal();
        showSuccess('Lesson marked as Incomplete');
        pushInCompleteEvent(true, contentId, completion?.id!);
      });
    } else {
      updateAssessmentInPlanner({
        variables: {
          input: {
            classId: currentClass.id,
            assessmentId: contentId,
            status: COMPLETION_STATUS_NOT_STARTED,
            unitId,
          },
        },
      }).then(res => {
        closeModal();
        if (res.data?.updateAssessmentInPlanner.assessment.completion) {
          pushInCompleteEvent(false, contentId, completion?.id!);
        }
        showSuccess('Assessment marked as Incomplete');
      });
    }
    pushRemoveCompleteFollow();
  }, [
    contentId,
    isLesson,
    unitId,
    completion,
    currentClass,
    closeModal,
    showSuccess,
    updateLessonInPlanner,
    updateAssessmentInPlanner,
  ]);

  return (
    <SimpleModal
      isVisible
      isOverflowable
      dataPushOnClose={false}
      title="Edit"
      headerTitle="Edit"
      dataTrackingLabel={EVENT_TARGET_IDENTIFIER_CONTENT_EDIT_COMPLETE}
      onClose={withCloseEventData(closeModal)}
    >
      <SimpleModalContentStyled>
        {isConfirmModalVisible ? (
          <>
            <PStyled>
              Are you sure you want to mark{' '}
              <AposStyled>{contentName}</AposStyled> as incomplete? Any dates
              entered about this {isLesson ? 'Lesson' : 'Assessment'} will be
              lost.
            </PStyled>
            <SimpleModalVerticalActionsStyled>
              <NegativeButton
                type="button"
                data-selector="mark-as-incomplete-button"
                loading={lessonCompletionLoading || assessmentCompletionLoading}
                disabled={
                  lessonCompletionLoading || assessmentCompletionLoading
                }
                onClick={handleMarkIncomplete}
              >
                Incomplete
              </NegativeButton>
              <SecondaryButton
                type="button"
                data-selector="cancel-btn"
                onClick={withCloseEventData(closeModal)}
              >
                Cancel
              </SecondaryButton>
            </SimpleModalVerticalActionsStyled>
          </>
        ) : (
          <FormProvider {...formMethods}>
            <form onSubmit={handleSubmit(onSubmit)}>
              {isInPlanner ? (
                <PStyled>
                  When did you teach this {isLesson ? 'Lesson' : 'Assessment'}?
                </PStyled>
              ) : (
                <>
                  <Heading16 as="h2" marginBottom={spacing4}>
                    When did you teach this {isLesson ? 'Lesson' : 'Assessment'}
                    ?
                  </Heading16>
                  <PStyled>
                    Completed {isLesson ? 'Lessons' : 'Assessments'} can be
                    viewed from this class&rsquo; Planner.
                  </PStyled>
                </>
              )}
              <DatePickerRangeFields
                isRequired
                fromFieldName={START_DATE_FIELD}
                toFieldName={END_DATE_FIELD}
                initialFromValue={toIsoDateFormat(completionStartDate)}
                initialToValue={toIsoDateFormat(completionEndDate)}
                minimumStartDate={start}
                maximumEndDate={end}
              />

              {(lessonCompletionError || assessmentCompletionError) && (
                <InlineServerError />
              )}
              <SimpleModalVerticalActionsStyled>
                <PrimaryButton
                  data-selector="update-mark-complete-button"
                  loading={
                    lessonCompletionLoading || assessmentCompletionLoading
                  }
                  disabled={
                    lessonCompletionLoading || assessmentCompletionLoading
                  }
                >
                  Update
                </PrimaryButton>
                <LinkButton
                  type="button"
                  onClick={withCloseEventData(() => {
                    pushDataTrackerEvent(
                      EVENT_CATEGORY_CONTENT,
                      EVENT_ACTION_OPEN,
                      {
                        target_type: EVENT_TARGET_TYPE_MODAL,
                        target_identifier:
                          EVENT_TARGET_IDENTIFIER_CONTENT_REMOVE_COMPLETE,
                      }
                    );
                    openConfirmModal();
                  })}
                  data-selector="mark-as-incomplete-button"
                >
                  Incomplete
                </LinkButton>
              </SimpleModalVerticalActionsStyled>
            </form>
          </FormProvider>
        )}
      </SimpleModalContentStyled>
    </SimpleModal>
  );
};

export default Incomplete;
